import * as THREE from 'three';
import type { Vec3 } from '../../sim/mathx';
import type { InterpolatedTelemetry } from '../../state/interpolate';
import type { TelemetryTrack } from '../../state/telemetryBuffer';
import type { TrackState } from '../../sim/trackTypes';
import { setThreePositionFromEnu } from '../entities/ScoutView';

export const TRACK_MARKER_POOL_CAP = 16;
export const TRACK_HISTORY_CAPACITY = 64;
export const DROPPED_TRACK_FADE_SECONDS = 1;
export const VELOCITY_ARROW_SECONDS = 0.5;

export const TRACK_STATE_COLORS: Readonly<Record<TrackState, number>> = Object.freeze({
  tentative: 0x76a7bd,
  confirmed: 0x4dff9a,
  coasting: 0xffb547,
  dropped: 0xff6b6b,
});

const TRACK_STATE_OPACITY: Readonly<Record<TrackState, number>> = Object.freeze({
  tentative: 0.35,
  confirmed: 1,
  coasting: 0.9,
  dropped: 0.75,
});

type InterpolatedTrack = InterpolatedTelemetry['tracks'][number];

/** One persistent marker; all frequently changed geometry is updated in place. */
export class TrackMarkerView extends THREE.Group {
  readonly groundMaterial: THREE.MeshBasicMaterial;
  readonly groundMarker: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  readonly stalkGeometry: THREE.BufferGeometry;
  readonly stalk: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  readonly labelAnchor = new THREE.Object3D();
  readonly velocityArrow: THREE.ArrowHelper;
  readonly historyGeometry: THREE.BufferGeometry;
  readonly history: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;

  labelText = '';
  opacity = 1;
  velocityArrowLength = 0;
  velocityDirection: Vec3 = { x: 0, y: 0, z: 0 };

  private markerId: number;
  private state: TrackState = 'tentative';
  private fading = false;
  private fadeElapsed = 0;
  private historyStart = 0;
  private historyCount = 0;
  private readonly historyPositions = new Float32Array(TRACK_HISTORY_CAPACITY * 3);
  private disposed = false;

  constructor(id: number) {
    super();
    this.markerId = id;
    this.name = `radar-viz:track:${id}`;

    this.groundMaterial = new THREE.MeshBasicMaterial({
      color: TRACK_STATE_COLORS.tentative,
      transparent: true,
      opacity: TRACK_STATE_OPACITY.tentative,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.groundMarker = new THREE.Mesh(new THREE.RingGeometry(0.55, 0.8, 24), this.groundMaterial);
    this.groundMarker.name = 'radar-viz:track-ground';
    this.groundMarker.position.z = 0.025;
    this.add(this.groundMarker);

    this.stalkGeometry = new THREE.BufferGeometry();
    const stalkPosition = new THREE.BufferAttribute(new Float32Array(6), 3);
    stalkPosition.setUsage(THREE.DynamicDrawUsage);
    this.stalkGeometry.setAttribute('position', stalkPosition);
    this.stalk = new THREE.Line(
      this.stalkGeometry,
      new THREE.LineBasicMaterial({ color: TRACK_STATE_COLORS.tentative, transparent: true }),
    );
    this.stalk.name = 'radar-viz:track-stalk';
    this.stalk.frustumCulled = false;
    this.add(this.stalk);

    this.labelAnchor.name = 'radar-viz:track-label-anchor';
    this.add(this.labelAnchor);

    this.velocityArrow = new THREE.ArrowHelper(
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(),
      1,
      TRACK_STATE_COLORS.tentative,
      0.25,
      0.12,
    );
    this.velocityArrow.name = 'radar-viz:track-velocity';
    this.add(this.velocityArrow);

    this.historyGeometry = new THREE.BufferGeometry();
    const historyPosition = new THREE.BufferAttribute(
      new Float32Array(TRACK_HISTORY_CAPACITY * 3),
      3,
    );
    historyPosition.setUsage(THREE.DynamicDrawUsage);
    this.historyGeometry.setAttribute('position', historyPosition);
    this.historyGeometry.setDrawRange(0, 0);
    this.history = new THREE.Line(
      this.historyGeometry,
      new THREE.LineBasicMaterial({
        color: TRACK_STATE_COLORS.tentative,
        transparent: true,
        opacity: 0.5,
      }),
    );
    this.history.name = 'radar-viz:track-history';
    this.history.frustumCulled = false;
    this.add(this.history);
  }

  get trackId(): number {
    return this.markerId;
  }

  get trackState(): TrackState {
    return this.state;
  }

  get isFading(): boolean {
    return this.fading;
  }

  get historyPointCount(): number {
    return this.historyCount;
  }

  updateTrack(track: TelemetryTrack, position: Vec3, mountEnu: Vec3): void {
    this.state = track.state;
    setThreePositionFromEnu(this, { x: position.x, y: position.y, z: 0 });
    this.writeStalk(position.z);
    this.labelAnchor.position.set(0, 0, position.z + 1.1);

    const dx = position.x - mountEnu.x;
    const dy = position.y - mountEnu.y;
    const dz = position.z - mountEnu.z;
    const range = Math.hypot(dx, dy, dz);
    const speed = Math.hypot(track.velEnu.x, track.velEnu.y, track.velEnu.z);
    this.labelText = `T${track.id}  ${Math.round(range)} m  ${speed.toFixed(1)} m/s`;
    this.updateVelocity(track.velEnu, speed, position.z);
    this.pushHistory(position);
    this.writeHistory(position);
    this.applyStyle();
  }

  beginFade(): void {
    if (this.fading) return;
    this.fading = true;
    this.fadeElapsed = 0;
  }

  cancelFade(): void {
    this.fading = false;
    this.fadeElapsed = 0;
    this.applyStyle();
  }

  advanceFade(dtSeconds: number): boolean {
    if (!this.fading) return false;
    this.fadeElapsed += dtSeconds;
    this.applyStyle();
    return this.fadeElapsed >= DROPPED_TRACK_FADE_SECONDS;
  }

  reuse(id: number): void {
    if (this.disposed) throw new Error('Cannot reuse a disposed TrackMarkerView');
    this.markerId = id;
    this.name = `radar-viz:track:${id}`;
    this.visible = true;
    this.fading = false;
    this.fadeElapsed = 0;
    this.clearHistory();
  }

  prepareForPool(): void {
    this.removeFromParent();
    this.visible = false;
    this.clearHistory();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    this.traverse((object) => {
      if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
        geometries.add(object.geometry);
        const objectMaterials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        for (const material of objectMaterials) materials.add(material);
      }
    });
    for (const geometry of geometries) geometry.dispose();
    for (const material of materials) material.dispose();
    this.clear();
  }

  private writeStalk(altitude: number): void {
    const position = this.stalkGeometry.getAttribute('position') as THREE.BufferAttribute;
    position.setXYZ(0, 0, 0, 0);
    position.setXYZ(1, 0, 0, altitude);
    position.needsUpdate = true;
    this.stalkGeometry.computeBoundingSphere();
  }

  private updateVelocity(velocity: Vec3, speed: number, altitude: number): void {
    this.velocityArrow.position.set(0, 0, altitude);
    this.velocityArrowLength = speed * VELOCITY_ARROW_SECONDS;
    if (speed < 1e-9) {
      this.velocityDirection = { x: 0, y: 0, z: 0 };
      this.velocityArrow.visible = false;
      return;
    }
    this.velocityDirection = {
      x: velocity.x / speed,
      y: velocity.y / speed,
      z: velocity.z / speed,
    };
    this.velocityArrow.visible = true;
    this.velocityArrow.setDirection(
      new THREE.Vector3(
        this.velocityDirection.x,
        this.velocityDirection.y,
        this.velocityDirection.z,
      ),
    );
    this.velocityArrow.setLength(
      this.velocityArrowLength,
      Math.min(0.6, this.velocityArrowLength * 0.25),
      Math.min(0.3, this.velocityArrowLength * 0.12),
    );
  }

  private pushHistory(position: Vec3): void {
    let index: number;
    if (this.historyCount < TRACK_HISTORY_CAPACITY) {
      index = (this.historyStart + this.historyCount) % TRACK_HISTORY_CAPACITY;
      this.historyCount += 1;
    } else {
      index = this.historyStart;
      this.historyStart = (this.historyStart + 1) % TRACK_HISTORY_CAPACITY;
    }
    const offset = index * 3;
    this.historyPositions[offset] = position.x;
    this.historyPositions[offset + 1] = position.y;
    this.historyPositions[offset + 2] = position.z;
  }

  private writeHistory(currentPosition: Vec3): void {
    const attribute = this.historyGeometry.getAttribute('position') as THREE.BufferAttribute;
    const output = attribute.array as Float32Array;
    for (let outputIndex = 0; outputIndex < this.historyCount; outputIndex += 1) {
      const historyIndex = (this.historyStart + outputIndex) % TRACK_HISTORY_CAPACITY;
      const source = historyIndex * 3;
      const target = outputIndex * 3;
      output[target] = this.historyPositions[source]! - currentPosition.x;
      output[target + 1] = this.historyPositions[source + 1]! - currentPosition.y;
      output[target + 2] = this.historyPositions[source + 2]!;
    }
    attribute.needsUpdate = true;
    this.historyGeometry.setDrawRange(0, this.historyCount);
  }

  private applyStyle(): void {
    const fade = this.fading ? Math.max(0, 1 - this.fadeElapsed / DROPPED_TRACK_FADE_SECONDS) : 1;
    const baseOpacity = TRACK_STATE_OPACITY[this.state];
    this.opacity = baseOpacity * fade;
    const color = TRACK_STATE_COLORS[this.state];
    this.groundMaterial.color.setHex(color);
    this.groundMaterial.opacity = this.opacity;
    this.stalk.material.color.setHex(color);
    this.stalk.material.opacity = this.opacity;
    this.history.material.color.setHex(color);
    this.history.material.opacity = this.opacity * 0.5;
    this.velocityArrow.setColor(new THREE.Color(color));
    const arrowMaterials = [
      ...(Array.isArray(this.velocityArrow.line.material)
        ? this.velocityArrow.line.material
        : [this.velocityArrow.line.material]),
      ...(Array.isArray(this.velocityArrow.cone.material)
        ? this.velocityArrow.cone.material
        : [this.velocityArrow.cone.material]),
    ];
    for (const material of arrowMaterials) {
      material.transparent = true;
      material.opacity = this.opacity;
    }
  }

  private clearHistory(): void {
    this.historyStart = 0;
    this.historyCount = 0;
    this.historyGeometry.setDrawRange(0, 0);
  }
}

/** Diffs telemetry ids, retains fading markers, and bounds the reuse pool. */
export class TrackMarkers extends THREE.Group {
  private readonly mountEnu: Vec3;
  private readonly active = new Map<number, TrackMarkerView>();
  private readonly pool: TrackMarkerView[] = [];
  private readonly retiredDroppedIds = new Set<number>();
  private disposed = false;

  constructor(mountEnu: Vec3) {
    super();
    this.name = 'radar-viz:tracks';
    this.mountEnu = { ...mountEnu };
  }

  get activeCount(): number {
    return this.active.size;
  }

  get pooledCount(): number {
    return this.pool.length;
  }

  getMarker(id: number): TrackMarkerView | undefined {
    return this.active.get(id);
  }

  update(
    rawTracks: readonly TelemetryTrack[],
    interpolatedTracks: readonly InterpolatedTrack[],
    dtSeconds: number,
  ): void {
    if (this.disposed) return;
    const dt = Number.isFinite(dtSeconds) && dtSeconds > 0 ? dtSeconds : 0;
    const rawById = new Map(rawTracks.map((item) => [item.id, item]));
    const interpolatedById = new Map(interpolatedTracks.map((item) => [item.id, item]));

    for (const retiredId of this.retiredDroppedIds) {
      const item = rawById.get(retiredId);
      if (!item || item.state !== 'dropped') this.retiredDroppedIds.delete(retiredId);
    }

    for (const raw of rawTracks) {
      if (raw.state === 'dropped' && this.retiredDroppedIds.has(raw.id)) continue;
      let marker = this.active.get(raw.id);
      if (!marker) {
        marker = this.acquire(raw.id);
        this.active.set(raw.id, marker);
        this.add(marker);
      }
      const renderPosition = interpolatedById.get(raw.id)?.posEnu ?? raw.posEnu;
      marker.updateTrack(raw, renderPosition, this.mountEnu);
      if (raw.state === 'dropped') marker.beginFade();
      else marker.cancelFade();
    }

    for (const [id, marker] of this.active) {
      if (!rawById.has(id)) marker.beginFade();
      if (!marker.advanceFade(dt)) continue;
      const wasDropped = rawById.get(id)?.state === 'dropped';
      this.active.delete(id);
      if (wasDropped) this.retiredDroppedIds.add(id);
      this.release(marker);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    for (const marker of this.active.values()) marker.dispose();
    for (const marker of this.pool) marker.dispose();
    this.active.clear();
    this.pool.length = 0;
    this.retiredDroppedIds.clear();
    this.clear();
  }

  private acquire(id: number): TrackMarkerView {
    const marker = this.pool.pop();
    if (!marker) return new TrackMarkerView(id);
    marker.reuse(id);
    return marker;
  }

  private release(marker: TrackMarkerView): void {
    marker.prepareForPool();
    if (this.pool.length < TRACK_MARKER_POOL_CAP) this.pool.push(marker);
    else marker.dispose();
  }
}
